import {gql} from 'graphql-request';
import {useCallback} from 'react';
import {MyData, UserProfile, VerifySmsCodeData} from '@/interface';
import {QueryFunctionContext, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useGqlRequest} from './request';

export const ME_QUERY_KEY = 'session/me';

export const useLogin = () => {
  const request = useGqlRequest({
    query: MUTATION_LOGIN,
  });

  return useMutation({
    mutationFn: (record: {email: string; password: string}) => request(record),
  });
};

export const useCheckUserExists = (email?: string) => {
  const request = useGqlRequest({
    query: USER_EXIST,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: boolean;
      }> => {
    if (!email?.length) {
      return;
    }

    return request({email});
  };

  return useQuery({
    queryFn,
    queryKey: ['session/user-exists', email],
    enabled: false,
  });
};

export const useRequestSmsCode = () => {
  const request = useGqlRequest({
    query: REQUEST_SMS_CODE,
  });

  const mutationFn = (record: {
    cellPhone: string;
    smsVerificationId?: string;
  }): Promise<{
    result?: VerifySmsCodeData;
  }> => {
    return request(record);
  };

  return useMutation({
    mutationFn,
  });
};

export const useRequestPwdResetCode = () => {
  const request = useGqlRequest({
    query: REQUEST_PWD_RESET_CODE,
  });

  const mutationFn = (record: {
    email: string;
    cellPhone: string;
    smsVerificationId?: string;
  }): Promise<{
    result?: VerifySmsCodeData;
  }> => {
    return request(record);
  };

  return useMutation({
    mutationFn,
  });
};

export const useVarifySmsCode = () => {
  const request = useGqlRequest({
    query: VERIFY_SMS_CODE,
  });

  const mutationFn = (record: {
    cellPhone: string;
    smsVerificationId?: string;
  }): Promise<{
    result?: {
      success: boolean;
      message: string;
      code: number;
    };
  }> => {
    return request(record);
  };

  return useMutation({
    mutationFn,
  });
};

export const useQueryMe = (disableAutoRefetch?: boolean) => {
  const request = useGqlRequest({
    query: QUERY_ME,
  });

  const queryFn = (
    context: QueryFunctionContext
  ):
    | undefined
    | Promise<{
        result?: MyData;
      }> => {
    return request(context);
  };

  return useQuery({
    queryFn,
    queryKey: [ME_QUERY_KEY],
    staleTime: disableAutoRefetch ? Infinity : 1 * 60 * 60 * 1000,
    refetchInterval: disableAutoRefetch ? false : 1 * 60 * 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: !disableAutoRefetch,
  });
};

export const useQueryMeObserver = () => {
  const request = useGqlRequest({
    query: QUERY_ME,
  });

  const queryFn = (
    context: QueryFunctionContext
  ):
    | undefined
    | Promise<{
        result?: MyData;
      }> => {
    return request(context);
  };

  return useQuery({
    queryFn,
    queryKey: [ME_QUERY_KEY],
    enabled: false,
  });
};

export const useRefetchMe = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries({queryKey: [ME_QUERY_KEY]});
  }, [queryClient]);
};

export const useLogout = () => {
  const request = useGqlRequest({
    query: LOGOUT,
  });
  const queryClient = useQueryClient();

  const mutationFn = () => {
    return request({});
  };

  return useMutation({
    mutationFn,
    onSettled: () => {
      queryClient.setQueryData([ME_QUERY_KEY], () => ({}));
    },
  });
};

export const useJoin = () => {
  const request = useGqlRequest({
    query: JOIN,
  });
  const refetchMe = useRefetchMe();

  const mutationFn = (record: {
    email: string;
    password: string;
    cellPhone: string;
    profile: UserProfile;
    smsVerificationId: string;
    isMarketingAgreed: boolean;
  }): Promise<{
    result?: MyData;
  }> => {
    return request(record);
  };

  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      if (data?.result?.id) {
        refetchMe();
      }
    },
  });
};

export const useResetPassword = () => {
  const request = useGqlRequest({
    query: UPDATE_PASSWORD,
  });

  const mutationFn = (record: {
    password: string;
    cellPhone: string;
    smsVerificationId: string;
  }): Promise<{
    result?: MyData;
  }> => {
    return request(record);
  };

  return useMutation({
    mutationFn,
  });
};

export const USER_PROFILE_RESULT = `
  ageGroup
  category
  gender
  other
  otherCategory
  purpose
`;

const MY_DATA_RESULT = `
  cellPhone
  email
  nickname
  id
  isActive
  isMarketingAgreed
  lastSessionAt
  metadata
  password
  refreshToken
  smsVerificationId
  tier
  transactionId
  useBeganAt
  useEndedAt
  profile {
    ${USER_PROFILE_RESULT}
  }
  billing {
    authenticatedAt
    cardCompany
    cardNumber
    method
    status
    productId
    productType
    nextTotalAmount
    orderName
    totalAmount
  }
  trialInfo {
    code
    type
    useBeganAt
    useEndedAt
    terminatedAt
  }
`;

const QUERY_ME = gql`
  query me {
    result: me {
      ${MY_DATA_RESULT}
    }
  }
`;

const MUTATION_LOGIN = gql`
  mutation login($email: String!, $password: String!) {
    result: login(email: $email, password: $password)
  }
`;

const USER_EXIST = gql`
  query checkUserExists($email: String!) {
    result: checkUserExists(email: $email)
  }
`;

const REQUEST_SMS_CODE = gql`
  mutation requestSmsCode($cellPhone: String!, $smsVerificationId: ID) {
    result: requestSmsCode(
      cellPhone: $cellPhone
      smsVerificationId: $smsVerificationId
    ) {
      smsVerificationId # 생성한 인증DB ID
      requestTime #인증시간
      requestId #인증요청 ID
    }
  }
`;

const REQUEST_PWD_RESET_CODE = gql`
  mutation requestPasswordResetCode(
    $cellPhone: String!
    $smsVerificationId: ID
    $email: String
  ) {
    result: requestPasswordResetCode(
      cellPhone: $cellPhone
      smsVerificationId: $smsVerificationId
      email: $email
    ) {
      requestId
      requestTime
      smsVerificationId
    }
  }
`;

const VERIFY_SMS_CODE = gql`
  mutation verificationSmsCode($code: String!, $smsVerificationId: ID!) {
    result: verificationSmsCode(
      code: $code
      smsVerificationId: $smsVerificationId
    ) {
      success
      message
      code
    }
  }
`;

const UPDATE_PASSWORD = gql`
  mutation updatePassword(
    $cellPhone: String!
    $password: String!
    $smsVerificationId: String!
  ) {
    result: updatePassword(
      cellPhone: $cellPhone
      password: $password
      smsVerificationId: $smsVerificationId
    )
  }
`;

const JOIN = gql`
  mutation join(
    $cellPhone: String!
    $email: String!
    $smsVerificationId: String!
    $isMarketingAgreed: Boolean
    $password: String!
    $profile: UserProfileInput
  ) {
    result: join(
      cellPhone: $cellPhone
      email: $email
      smsVerificationId: $smsVerificationId
      isMarketingAgreed: $isMarketingAgreed
      password: $password
      profile: $profile
    ) {
      ${MY_DATA_RESULT}
    }
  }
`;

export const LOGOUT = gql`
  mutation logout {
    result: logout
  }
`;

export const REFRESH_TOKEN = gql`
  mutation refresh {
    refresh
  }
`;
